import { AppointmentActions } from '@/constants/appointmentActions.ts';
import { cloneDeep, groupBy, sortBy } from 'es-toolkit';
import toast from 'react-hot-toast';

import { AppointmentStatus, languageMap } from '@/utils/helpers.js';

/**
 * Handles appointment actions like cancel and reschedule
 * @param {Object} params - Parameters object
 * @param {string} params.action - The action to perform (from AppointmentActions)
 * @param {Object} params.data - The appointment data
 * @param {Object} params.upComming - The upcoming appointment data
 * @param {Object} params.mainData - Main data with translations
 * @param {Function} params.setIsCancelDialogOpen - Function to set cancel dialog state
 * @param {Function} params.setIsRescheduleDialogOpen - Function to set reschedule dialog state
 * @param {Function} params.clearAction - Function to clear the current action
 */
export const handleAppointmentAction = ({
    action,
    data,
    upComming,
    mainData,
    setIsCancelDialogOpen,
    setIsRescheduleDialogOpen,
    clearAction,
}) => {
    if (action === AppointmentActions.CANCEL) {
        const canCancel = data?.status === AppointmentStatus.BOOKED && !upComming?.is_past;

        if (canCancel) {
            setIsCancelDialogOpen(true); // Open cancel dialog
        } else {
            toast.error(mainData.content.cancel_not_possible);
        }
        clearAction();
    } else if (action === AppointmentActions.RESCHEDULE) {
        const canReschedule = data?.status === AppointmentStatus.BOOKED && !upComming?.is_past;

        if (canReschedule) {
            setIsRescheduleDialogOpen(true); // Open reschedule dialog
        } else {
            toast.error(mainData.content.reschedule_not_possible);
        }
        clearAction();
    }
};

const CANCELLED_STATUSES = new Set([AppointmentStatus.CANCELLED, AppointmentStatus.DECLINED]);

const isCancelled = (appointment) => CANCELLED_STATUSES.has(appointment.status);

/**
 * Collection ids (series/chains) that still have at least one live step in the active bucket.
 *
 * The backend keeps cancelled-but-unacknowledged appointments in the active bucket, so a collection
 * whose every step is cancelled/declined can still show up there. Such collections belong to the
 * past tab, not the upcoming one.
 *
 * @param {Array<Object>} activeAppointments - the `active` bucket returned by the appointments API
 * @returns {Set<string>} collection ids with at least one non-cancelled appointment
 */
const getLiveCollectionIds = (activeAppointments) =>
    activeAppointments.reduce(
        (collectionIds, appointment) =>
            appointment.collection_id && !isCancelled(appointment)
                ? collectionIds.add(appointment.collection_id)
                : collectionIds,
        new Set()
    );

/**
 * Splits the appointments API buckets into the two lists rendered by the My Appointments tabs.
 *
 * @param {Array<Object>} [activeAppointments] - the `active` bucket returned by the appointments API
 * @param {Array<Object>} [inactiveAppointments] - the `inactive` bucket returned by the appointments API
 * @returns {{ upcoming: Array<Object>, past: Array<Object> }}
 */
export function splitAppointmentsIntoTabs(activeAppointments = [], inactiveAppointments = []) {
    const liveCollectionIds = getLiveCollectionIds(activeAppointments);

    const belongsToUpcoming = (appointment, isFromActiveBucket) =>
        appointment.collection_id ? liveCollectionIds.has(appointment.collection_id) : isFromActiveBucket;

    const upcoming = [
        ...activeAppointments.filter((appointment) => belongsToUpcoming(appointment, true)),
        ...inactiveAppointments.filter((appointment) => belongsToUpcoming(appointment, false)),
    ];
    const past = [
        ...activeAppointments.filter((appointment) => !belongsToUpcoming(appointment, true)),
        ...inactiveAppointments.filter((appointment) => !belongsToUpcoming(appointment, false)),
    ];

    return { upcoming: upcoming, past: past };
}

export function hydrateAppointments(appointments, doctorPrismicData, locationPrismicData, language) {
    for (const appointment of appointments) {
        const doctorDocuments = Object.values(doctorPrismicData ?? {}).filter(
            (document) => document?.key?.[0]?.text === appointment.professional.key
        );
        const doctor = {};
        for (const document of doctorDocuments) {
            doctor[languageMap[language]] = cloneDeep(document);
        }
        appointment.doctor = doctor;

        const locationDocuments = (locationPrismicData ?? []).filter(
            (document) => document.data.key[0].text === appointment.location_key
        );
        const location = {};
        for (const document of locationDocuments) {
            location[document.lang] = document.data;
        }
        appointment.location = location;
    }

    return appointments;
}

/**
 * @typedef {Object} CollectionAppointmentDuration
 * @property {number} value
 * @property {'MINUTES'} units
 */

/**
 * @typedef {Object} CollectionDataItem
 * @property {Date} date
 * @property {string} status
 * @property {boolean} isMainAppointment
 * @property {CollectionAppointmentDuration} duration
 * @property {Object} appointmentData
 */

/**
 * @typedef {Array<CollectionDataItem>} SeriesData
 */

/**
 * @typedef {Array<CollectionDataItem>} ChainData
 */

/**
 * Formats and sorts appointments, grouping collection appointments (series and chains) and adding metadata.
 *
 * This function processes a list of appointments by:
 * - Grouping appointments by their collection_id (series and chain appointments share the same collection_id)
 * - For chain appointments (any appointment in the group has `chain_configuration_id`): adds a `chainData` property
 * - For series appointments (collection_id without `chain_configuration_id`): adds a `seriesData` property
 * - For single appointments: keeps them as-is without seriesData/chainData
 * - Returns all appointments sorted by start date, ready for display
 *
 * @param {Array<Object>} appointments - Array of appointment objects
 * @param {string} [appointments[].collection_id] - Collection ID for grouped appointments (undefined for single appointments)
 * @param {string} [appointments[].chain_configuration_id] - Present on chain appointments
 * @param {string} appointments[].start - ISO date string for appointment start time
 * @param {string} appointments[].status - Appointment status (e.g., 'BOOKED', 'CANCELLED')
 * @param {'desc' | 'asc' } order - Order of the appointments
 *
 * @returns {Array.<Object & {seriesData?: SeriesData, chainData?: ChainData}>} Sorted array of appointments. Chain appointments include a `chainData` property. Series appointments include a `seriesData` property. Single appointments include neither.
 */
export function formatAppointments(appointments, order = 'asc') {
    const sortedAppointments = sortBy(appointments, [(appointment) => new Date(appointment.start)]);

    const singleAppointments = sortedAppointments.filter((appointment) => !appointment.collection_id);

    const appointmentsByCollectionId = groupBy(
        sortedAppointments.filter((appointment) => appointment.collection_id),
        (appointment) => appointment.collection_id
    );

    const seriesAppointments = [];
    const chainAppointments = [];

    Object.values(appointmentsByCollectionId).forEach((collectionGroup) => {
        const isChain = collectionGroup.some((appointment) => appointment.chain_configuration_id);

        const mainAppointment =
            collectionGroup.find((appointment) => appointment.status === AppointmentStatus.BOOKED) ??
            [...collectionGroup].reverse().find((appointment) => appointment.status === AppointmentStatus.COMPLETED) ??
            collectionGroup[collectionGroup.length - 1];

        const collectionData = collectionGroup.map((appointment) => ({
            date: new Date(appointment.start),
            status: appointment.status,
            isMainAppointment: appointment.id === mainAppointment.id,
            duration: appointment.duration,
            appointmentData: appointment,
        }));

        if (isChain) {
            chainAppointments.push({ ...mainAppointment, chainData: collectionData });
        } else {
            seriesAppointments.push({ ...mainAppointment, seriesData: collectionData });
        }
    });

    return sortBy(
        [...chainAppointments, ...seriesAppointments, ...singleAppointments],
        [
            (appointment) => {
                const time = new Date(appointment.start).getTime();
                return order === 'desc' ? -time : time;
            },
        ]
    );
}

export function formatDuration(value, unit) {
    const unitLabelMap = {
        MINUTES: 'min',
    };

    // Return a safe fallback if the value or unit is missing/unknown
    if (value == null || unit == null) {
        return '';
    }

    const unitLabel = unitLabelMap[unit];
    if (!unitLabel) {
        return '';
    }

    return `${String(value)} ${unitLabel}`;
}

export const appointmentStatusLabels = {
    [AppointmentStatus.CANCELLED]: 'Cancelled',
    [AppointmentStatus.BOOKED]: 'Confirmed',
    [AppointmentStatus.COMPLETED]: 'Past',
    [AppointmentStatus.DECLINED]: 'Declined',
};
